import React, { useState, useEffect } from "react";
import { fetchAllTrips } from "../../functions/FetchTrips";
import { fetchAllEnquiries } from "../../functions/Enquiry";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import Inquiries from "./Inquiries"; // Import the Inquiries component

const Dashboard = ({ navigateTo }) => {
  const [stats, setStats] = useState({
    activeTrips: 0,
    totalEnquiries: 0,
    totalReviews: 0, // Changed from totalBookings
    monthlyRevenue: 0 // Keep stat, but data source removed
  });
  
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch trips data
        const tripsData = await fetchAllTrips();
        const activeTrips = tripsData.filter(trip => trip.status === 'Active').length;
        
        // Fetch travel plan inquiries
        const inquiriesResponse = await fetchAllEnquiries();
        let totalInquiriesCount = 0;
        const inquiriesData = inquiriesResponse.success ? inquiriesResponse.data : [];
        totalInquiriesCount += inquiriesData.length;
        
        // Fetch booking inquiries from "Enquiries" collection
        try {
          const bookingEnquiriesCollectionRef = collection(db, 'Enquiries');
          const bookingQuerySnapshot = await getDocs(bookingEnquiriesCollectionRef);
          
          let bookingInquiriesCount = 0;
          
          // Count booking inquiries
          bookingQuerySnapshot.forEach((doc) => {
            const data = doc.data();
            Object.keys(data).forEach(key => {
              if (key.startsWith('entries.')) {
                bookingInquiriesCount++;
              }
            });
          });
          
          totalInquiriesCount += bookingInquiriesCount;
          
        } catch (bookingError) {
          // console.error("Error fetching booking inquiries:", bookingError);
        }

        // Fetch review count from Firestore
        let totalReviews = 0;
        try {
          const reviewsDocRef = doc(db, "reviews", "AllReviews");
          const reviewsDocSnap = await getDoc(reviewsDocRef);
          if (reviewsDocSnap.exists()) {
            const reviewsData = reviewsDocSnap.data();
            totalReviews = Object.keys(reviewsData || {}).length;
          }
        } catch (reviewError) {
          // console.error("Error fetching reviews:", reviewError);
        }
        
        // Get recent inquiries
        const sortedInquiries = [...inquiriesData].sort((a, b) => {
          const dateA = a.date && a.date !== 'N/A' ? new Date(a.date) : null;
          const dateB = b.date && b.date !== 'N/A' ? new Date(b.date) : null;
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          return dateB - dateA;
        });
        
        setRecentInquiries(sortedInquiries.slice(0, 5));
        
        // Update all stats with the combined inquiry count
        setStats({
          activeTrips,
          totalEnquiries: totalInquiriesCount,
          totalReviews,
          monthlyRevenue: 0
        });
        
      } catch (err) {
        // console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="dashboard-section fadeIn">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-section fadeIn">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-section fadeIn">
      {/* Info Cards remain the same */}
      <div className="info-cards-container mb-4">
        <div className="info-card bg-primary-light" onClick={() => navigateTo('trips')}>
          <div className="info-card-icon text-primary">
            <i className="fas fa-suitcase fa-2x"></i>
          </div>
          <div className="info-card-content">
            <h4 className="info-card-value">{stats.activeTrips}</h4>
            <p className="info-card-label">Active Trips</p>
          </div>
        </div>
        <div className="info-card bg-success-light" onClick={() => navigateTo('inquiries')}>
          <div className="info-card-icon text-success">
            <i className="fas fa-users fa-2x"></i>
          </div>
          <div className="info-card-content">
            <h4 className="info-card-value">{stats.totalEnquiries}</h4>
            <p className="info-card-label">Total Enquiries</p>
          </div>
        </div>
        <div className="info-card bg-info-light" onClick={() => navigateTo('reviews')}>
          <div className="info-card-icon text-info">
            <i className="fas fa-star fa-2x"></i>
          </div>
          <div className="info-card-content">
            <h4 className="info-card-value">{stats.totalReviews}</h4>
            <p className="info-card-label">Reviews</p>
          </div>
        </div>
        <div className="info-card bg-warning-light" onClick={() => navigateTo('categories')}>
          <div className="info-card-icon text-warning">
            <i className="fas fa-list fa-2x"></i>
          </div>
          <div className="info-card-content">
            <h4 className="info-card-value"></h4>
            <p className="info-card-label">Add Categories</p>
          </div>
        </div>
        {/* New card for Update Images */}
        <div className="info-card bg-secondary-light" onClick={() => navigateTo('updateImages')}>
          <div className="info-card-icon text-secondary">
            <i className="fas fa-image fa-2x"></i>
          </div>
          <div className="info-card-content">
            <h4 className="info-card-value"></h4>
            <p className="info-card-label">Update Images</p>
          </div>
        </div>
      </div>

      {/* Recent Enquiries Section using Inquiries component */}
      <div className="admin-card mb-4">
        <div className="card-header">
          <h3>Recent Enquiries</h3>
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigateTo('inquiries')}
          >
            View All Enquiries
          </button>
        </div>
        <div className="card-body p-0">
          {/* Render Inquiries component with a prop to limit to 5 */}
          <Inquiries limit={5} hideHeader hideFilter />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
